import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { imageUrl } from "../../../api/apiConfig/apiConfig";
import "../Home/Homepage.css";
import "./WithdrawalFeesPage.css";

/** Build absolute URL for coin image paths like icons/... */
const resolveCoinIconUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(imageUrl || "").replace(/\/$/, "");
  return `${base}/${p.replace(/^\//, "")}`;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const WithdrawalFeesPage = () => {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [search, setSearch] = useState("");
  const [aggregateFee, setAggregateFee] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const fetchWithdrawalFees = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await AdminService.getAdminWithdrawalFees({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (!res?.success) {
        setFees([]);
        setAggregateFee(null);
        setFetchError(res?.message || "Failed to load withdrawal fees");
        return;
      }

      const payload = res.data;
      const list =
        payload && typeof payload === "object" && Array.isArray(payload.list)
          ? payload.list
          : [];
      setFees(list);

      const agg = payload?.total_fee;
      setAggregateFee(agg != null && agg !== "" && !Number.isNaN(Number(agg)) ? Number(agg) : null);

      const totalCount = Number(res.totalCount ?? list.length) || 0;
      const totalPages = Math.max(
        1,
        Number(res.totalPages) || (pagination.limit > 0 ? Math.ceil(totalCount / pagination.limit) : 1)
      );
      const currentPage = Number(res.currentPage ?? pagination.page) || 1;
      setPagination((p) => ({
        ...p,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    } catch {
      setFees([]);
      setAggregateFee(null);
      setFetchError("Failed to load withdrawal fees");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchWithdrawalFees();
  }, [fetchWithdrawalFees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return fees;
    return fees.filter((f) => {
      const uuid = String(f.uuid ?? "").toLowerCase();
      const email = String(f.emailId ?? f.email ?? "").toLowerCase();
      const shortName = String(f.short_name ?? "").toLowerCase();
      const fullName = `${f.firstName ?? ""} ${f.lastName ?? ""}`.trim().toLowerCase();
      const chain = String(f.chain ?? "").toLowerCase();
      return (
        uuid.includes(q) ||
        email.includes(q) ||
        shortName.includes(q) ||
        fullName.includes(q) ||
        chain.includes(q)
      );
    });
  }, [fees, search]);

  const goToPage = (next) => {
    if (next < 1 || next > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page: next }));
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Withdrawal Fees</span>
          </p>
          <h1 className="dashboard-hero__title">Withdrawal Fees</h1>
          <p className="dashboard-hero__subtitle">
            Set withdrawal fees by coin and network
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-percent" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="withdrawalfees-section">
          <div className="withdrawalfees-section__header">
            <div className="withdrawalfees-section__title-wrap">
              <span className="withdrawalfees-section__icon">
                <i className="fas fa-coins" />
              </span>
              <h2 className="withdrawalfees-section__title">Withdrawal Fee Records</h2>
            </div>
            <div className="withdrawalfees-section__stats">
              <span className="withdrawalfees-stat">
                <strong>{pagination.totalCount}</strong> Records
              </span>
              {aggregateFee != null && (
                <span className="withdrawalfees-stat">
                  <strong>{aggregateFee.toLocaleString("en-IN", { maximumFractionDigits: 8 })}</strong> Total fee
                </span>
              )}
            </div>
          </div>
          {fetchError && (
            <p className="withdrawalfees-empty withdrawalfees-empty--error" role="alert">{fetchError}</p>
          )}
          <div className="withdrawalfees-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by UUID, name, email, currency or chain..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="withdrawalfees-search-input"
            />
          </div>
          <div className="withdrawalfees-table-wrap">
            <table className="withdrawalfees-table">
              <thead>
                <tr>
                  <th>UUID</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Created</th>
                  <th>Currency</th>
                  <th>Chain</th>
                  <th>Total Amount</th>
                  <th>Total Fee</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="withdrawalfees-table__loading">
                      <i className="fas fa-spinner fa-spin" /> Loading...
                    </td>
                  </tr>
                ) : (
                  filtered.map((row, i) => {
                    const iconSrc = resolveCoinIconUrl(row.coin_image);
                    const fullName = [row.firstName, row.lastName].filter(Boolean).join(" ").trim() || "—";
                    return (
                      <tr key={`${row.user_id ?? "u"}-${row.createdAt ?? i}-${i}`}>
                        <td><span className="withdrawalfees-mono">{row.uuid ?? "—"}</span></td>
                        <td>{fullName}</td>
                        <td>{row.emailId ?? row.email ?? "—"}</td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>
                          <span className="withdrawalfees-currency">
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt={row.short_name ? `${row.short_name} icon` : ""}
                                className="withdrawalfees-currency__icon"
                                width={26}
                                height={26}
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <span className="withdrawalfees-currency__icon withdrawalfees-currency__icon--placeholder" aria-hidden>
                                <i className="fas fa-coins" />
                              </span>
                            )}
                            <strong className="withdrawalfees-currency__name">{row.short_name ?? "—"}</strong>
                          </span>
                        </td>
                        <td><span className="withdrawalfees-chain">{row.chain ?? "—"}</span></td>
                        <td>{typeof row.total_amount === "number" ? row.total_amount.toLocaleString("en-IN", { maximumFractionDigits: 8 }) : (row.total_amount ?? "—")}</td>
                        <td>{typeof row.total_fee === "number" ? row.total_fee.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : (row.total_fee ?? "—")}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && filtered.length === 0 && !fetchError && (
            <p className="withdrawalfees-empty">
              {fees.length === 0 && !search.trim()
                ? "No withdrawal fee records yet."
                : "No withdrawal fee records match your search."}
            </p>
          )}
          {!loading && pagination.totalPages > 1 && (
            <div className="withdrawalfees-pagination">
              <button type="button" className="withdrawalfees-page-btn" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1}>
                Prev
              </button>
              <span>Page {pagination.page} of {pagination.totalPages}</span>
              <button type="button" className="withdrawalfees-page-btn" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}>
                Next
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default WithdrawalFeesPage;
